import Action from './Action';
import Conflict from './Conflict';
import ConflictFinder from './ConflictFinder';
import * as TickResolutionHelpers from './TickResolutionHelpers';
import type { AnyEffect, AttackEffect, BlockEffect, Proposal } from '../lib/Proposal';
import { ActionType, ResolvedAction, type AnimationStep } from '../lib/Action';
import ProjectedOccupancy, { TickResolutionContext as POContext } from './ProjectedOccupancy';
import { ActorStats, TakesTurns, Faction, HexPosition } from '../components';
import getLogger from '../lib/logger';
import HexBearing from '../grid/HexBearing';

const logger = getLogger('TickResolutionContext');

type MergedDefinition = {
  priority: number;
  effects: AnyEffect[];
  augmentProposals: Proposal[];
};

export default class TickResolutionContext implements POContext {
  public pendingActions: Action[];
  public resolvedActions: ResolvedAction[] = [];
  public masterAnimationPlan: AnimationStep[][] = [];
  public projectedOccupancy: ProjectedOccupancy;
  private initialOccupancy: ProjectedOccupancy;
  private _mergedDefinitionsForLoop: Map<number, MergedDefinition | null> = new Map();
  private _spentAnyEffects: Set<AttackEffect> = new Set();
  private mergedEffects: Map<number, AnyEffect[]> = new Map(); // entity.id -> effects
  private pendingDeaths: Set<number> = new Set();

  /**
   * @param actions - The list of all actions to be resolved this tick.
   * @param projectedOccupancy - The initial state of entity positions for the tick.
   */
  constructor(actions: Action[], projectedOccupancy: ProjectedOccupancy) {
    this.pendingActions = [...actions];
    this.projectedOccupancy = projectedOccupancy;
    this.initialOccupancy = new ProjectedOccupancy();
    this.initialOccupancy.setInitialOccupancy(this.projectedOccupancy.getAllOccupants());

    for (const action of actions) {
      if (!action.startPos) {
        // This can happen for entities that don't start on the board, which is valid.
        continue;
      }
      const occupant = this.projectedOccupancy.getOccupant(action.startPos);
      if (!occupant || occupant.id !== action.entity.id) {
        throw new Error(
          `Action entity ${action.entity.id} with startPos ${action.startPos.key} not found in projected occupancy.`
        );
      }
    }
  }

  public isActionForEntityResolved(entityId: number): boolean {
    return this.resolvedActions.some(a => a.entity.id === entityId);
  }

  public resolve(): { resolvedActions: Action[], animationPlan: AnimationStep[][] } {
    let loopCount = 0;
    const maxLoops = this.pendingActions.length * 2 + 5; // Safeguard

    while (this.pendingActions.length > 0) {
      this._mergedDefinitionsForLoop.clear();
      logger.debug(`--- TOP OF LOOP #${loopCount} ---`);
      const resolvedThisLoop: Action[] = [];
      logger.debug(`\n--- Resolution Loop #${loopCount + 1} ---`);
      logger.debug(`Pending actions (${this.pendingActions.length}):`, this.pendingActions.map(a => a.toString()));
      this._logOccupancy();

      if (loopCount++ > maxLoops) {
        logger.warn("Resolution loop limit reached. Idling remaining actions.");
        this.pendingActions.forEach((action: Action) => {
          action.forceIdle();
          action.resolve();
          this.resolvedActions.push(action);
          logger.debug("Resolved actions:", this.resolvedActions.map(a => a.toString()))
          resolvedThisLoop.push(action);
        });
        this.pendingActions = []; // Clear the queue
        continue; // Exit the loop
      }

      // Pre-emptively resolve any movers that are targeting a hex occupied by an *already resolved* actor.
      if (this._resolvePreemptiveBonks()) {
        continue; // If bonks were resolved, restart the main loop.
      }

      // Now that pre-emptive bonks are handled, we create implicit idles and gather proposals.
      const pendingEntityIds = new Set(this.pendingActions.map(a => a.entity.id));
      const implicitIdleActions: Action[] = [];
      for (const entity of this.projectedOccupancy.getAllOccupants()) {
        if (!pendingEntityIds.has(entity.id) && entity.hasComponent(TakesTurns)) {
          implicitIdleActions.push(new Action(entity, ActionType.IDLE, {}));
        }
      }

      const allConsideredActions = [...this.pendingActions, ...implicitIdleActions];
      const allProposals = TickResolutionHelpers.gatherProposals(allConsideredActions);

      for (const action of allConsideredActions) {
        this._mergedDefinitionsForLoop.set(action.entity.id, action.getMergedDefinition(allProposals));
      }

      const conflictObjects = new ConflictFinder(allConsideredActions, this.projectedOccupancy, this.resolvedActions).findAll();

      if (conflictObjects.length === 0) {
        if (this.pendingActions.length === 0) continue;

        // In a no-conflict scenario, we must resolve actions in a safe order.
        // A "safe" action is one that doesn't move into a hex occupied by another
        // PENDING actor. This correctly resolves conga lines from front to back.
        const pendingEntityPosKeys = new Set<string>();
        for (const action of this.pendingActions) {
          if (action.startPos) {
            pendingEntityPosKeys.add(action.startPos.key);
          }
        }

        let safeActions = this.pendingActions.filter(action => {
          if (action.type !== ActionType.MOVE) return true; // Non-moves are safe
          const dest = action.details.coords;
          return !dest || !pendingEntityPosKeys.has(dest.key);
        });

        if (safeActions.length === 0) {
          // This implies a dependency cycle that ConflictFinder missed (e.g. a swap).
          // This shouldn't happen, but as a safeguard, we'll break the cycle by
          // resolving the highest priority action among all pending actions.
          logger.warn("No safe actions found in no-conflict scenario. This may be a cycle. Breaking it.");
          safeActions = this.pendingActions;
        }

        const winner = TickResolutionHelpers.determineWinner(safeActions, allProposals);
        if (this._resolveActionForPhase1(winner)) {
          continue; // Action was re-queued, restart loop.
        }
        this.pendingActions = this.pendingActions.filter(a => a.id !== winner.id);
      } else {
        if (this.resolveHighestPriorityConflict(conflictObjects, allProposals)) {
          continue; // Action was re-queued, restart loop.
        }
      }
    }

    // --- Phase 2: Final-State Combat Resolution ---
    for (const action of this.resolvedActions) {
      this._resolveActionForPhase2(action);
    }

    // --- Final Animation Aggregation ---
    for (const action of this.resolvedActions) {
      if (!action.animationPlan || action.animationPlan.length === 0) continue;
      for (let i = 0; i < action.animationPlan.length; i++) {
        const componentStage = action.animationPlan[i];
        if (componentStage && componentStage.length > 0) {
          const targetStageIndex = action.minimumAnimationStage + i;
          if (!this.masterAnimationPlan[targetStageIndex]) {
            this.masterAnimationPlan[targetStageIndex] = [];
          }
          this.masterAnimationPlan[targetStageIndex].push(...componentStage);
        }
      }
    }

    return { resolvedActions: this.resolvedActions, animationPlan: this.masterAnimationPlan.filter(s => s && s.length > 0) };
  }

  private _logOccupancy(): void {
    const occupants = this.projectedOccupancy.getAllEntries().map(([hexKey, entity]) => `  - ${hexKey}: Actor ${entity.id}`);
    logger.debug(`Projected Occupancy:\n${occupants.join('\n')}`);
  }

  /**
   * Finds all current conflicts between pending actions.
   * A conflict occurs when:
   * 1. Multiple actors try to move into the same hex (head-on).
   * 2. An actor tries to move into a hex occupied by a stationary actor (bonk).
   * 3. Two actors try to swap hexes.
   *
   * This method only *detects* conflicts, it does not resolve them. It will not report a
   * conflict for a move into a hex occupied by an entity whose own action for the tick
   * has already been resolved, as that is handled as an immediate "bonk".
   *

  /**
   * Given a list of conflicts, it finds the one with the highest priority,
   * resolves it, updates the state (pending/resolved actions, occupancy),
   * and returns the actions that were resolved in this step.
   * @param conflicts - A list of all current conflicts.
   * @param allProposals - All outcome proposals for all pending actions.
   * @returns The list of actions that were resolved in this step.
   */
  resolveHighestPriorityConflict(conflicts: Conflict[], allProposals: { action: Action, proposal: Proposal }[]): boolean {
    if (conflicts.length === 0) return false;

    let bestConflict: { priority: number, conflict: Conflict | null } = { priority: -1, conflict: null };
    for (const conflict of conflicts) {
      let currentPriority = -1;
      for (const p of conflict.participants) {
        const mergedDefinition = this._mergedDefinitionsForLoop.get(p.entity.id);
        if (mergedDefinition && mergedDefinition.priority > currentPriority) {
          currentPriority = mergedDefinition.priority;
        }
      }
      if (currentPriority > bestConflict.priority) {
        bestConflict = { priority: currentPriority, conflict };
      }
    }

    logger.debug(`Found highest priority conflict: ${bestConflict.conflict!.toString()}`)

    const { participants } = bestConflict.conflict!;

    const winner: Action = TickResolutionHelpers.determineWinner(participants, allProposals);
    logger.debug(`  --> Winner: ${winner.toString()}`);

    if (this._resolveActionForPhase1(winner, bestConflict.conflict!)) {
      return true; // The winner was re-queued, so we restart the main loop.
    }

    const losers: Action[] = participants.filter(c => c.id !== winner.id);
    logger.debug("Handling losers:", losers.map(a => a.toString()));

    losers.forEach((loser: Action) => {
      loser.forceIdle();
    });

    this.pendingActions = this.pendingActions.filter(a => a.id !== winner.id);
    return false;
  }

  private _resolveActionForPhase1(action: Action, conflict?: Conflict): boolean {
    logger.debug(`Phase 1: Resolving action: ${action.toString()}`);
    const mergedDefinition = this._mergedDefinitionsForLoop.get(action.entity.id);
    logger.debug(`  - Merged definition:`, JSON.parse(JSON.stringify(mergedDefinition)));

    if (!mergedDefinition) {
      logger.debug(`  - No merged definition. Forcing IDLE.`);
      action.forceIdle();
      action.resolve();
      this.resolvedActions.push(action);
      return false;
    }

    const { effects: baseEffects, augmentProposals } = mergedDefinition;

    // Step 1: Process base effects (from replacement + defensive augments).
    const contributingAnimations: { plan: AnimationStep[][], strategy?: string }[] = [];
    if (this._processEffectsForPhase1(baseEffects, action, contributingAnimations, conflict, true)) {
      // Re-queue signaled
      logger.debug(`  - Reverting action for ${action.entity.id} to its original intent due to whiffed replacement.`);
      const index = this.pendingActions.findIndex(a => a.id === action.id);
      if (index > -1) {
        const newAction = new Action(action.entity, action.type, action.details);
        newAction.startPos = this.initialOccupancy.findEntity(action.entity);
        newAction.isFallback = true;
        this.pendingActions.splice(index, 1, newAction);
      }
      return true; // Signal to main loop to restart.
    }

    // Step 2: Finalize positioning from base effects. This may remove a MOVE outcome if it's a bonk.
    this._finalizeMoveOutcome(action, contributingAnimations);

    // Step 3: Conditionally process move-based augments.
    const finalEffects = [...baseEffects];
    if (action.outcomes.some(o => o.type === ActionType.MOVE)) {
      logger.debug(`  - Move outcome confirmed. Processing move-based augments.`);
      for (const augment of augmentProposals) {
        finalEffects.push(...augment.effects);
        // A whiffed augment attack should not cause a re-queue. We pass `false` for `isFromReplacement`
        // and ignore the return value, as re-queueing is not desired for augments.
        this._processEffectsForPhase1(augment.effects, action, contributingAnimations, conflict, false);
      }
    } else {
      logger.debug(`  - No valid move outcome. Skipping move-based augments.`);
    }

    this.mergedEffects.set(action.entity.id, finalEffects);

    // Step 4: Build the final animation plan for this action.
    action.animationPlan = TickResolutionHelpers.buildAnimationPlan(contributingAnimations);

    // Step 5: Update occupancy map for any *final* move.
    const finalMoveOutcome = action.outcomes.find(o => o.type === ActionType.MOVE);
    if (finalMoveOutcome && action.startPos) {
      const currentPos = this.projectedOccupancy.findEntity(action.entity) || action.startPos;
      logger.debug(`  - Move is valid. Updating projected occupancy from ${currentPos.key} to ${finalMoveOutcome.coords.key}.`);
      this.projectedOccupancy.moveEntity(action.entity, currentPos, finalMoveOutcome.coords, this);
    }

    // Step 6: Now that all outcomes for this phase are final, resolve the action.
    logger.debug(`  - Final outcomes for action:`, JSON.parse(JSON.stringify(action.outcomes)));
    action.resolve();
    this.resolvedActions.push(action);
    return false;
  }

  private _processEffectsForPhase1(effects: AnyEffect[], action: Action, contributingAnimations: { plan: AnimationStep[][], strategy?: string }[], conflict: Conflict | undefined, isFromReplacement: boolean): boolean {
    for (const effect of effects) {
      logger.debug(`  - Processing effect:`, effect);
      if (effect.type === 'MOVE') {
        action.addMoveOutcome(effect.coords);
        if (effect.animationPlan) contributingAnimations.push({ plan: effect.animationPlan });
        logger.debug(`  - Processed MOVE effect. Outcomes are now:`, JSON.parse(JSON.stringify(action.outcomes)));
      } else if (effect.type === 'TURN') {
        action.addTurnOutcome(effect.bearing);
        if (effect.animationPlan) contributingAnimations.push({ plan: effect.animationPlan });
      } else if (effect.type === 'IDLE') {
        action.addIdleOutcome();
      } else if (effect.type === 'ATTACK' && (effect.attackTiming === 'START' || effect.attackTiming === 'ANY')) {
        logger.debug(`  - Processing ATTACK(START) effect.`);
        const potentialTarget = this.initialOccupancy.getOccupant(effect.attackHex);
        const target = (potentialTarget && potentialTarget.getComponent(Faction)?.name !== action.entity.getComponent(Faction)?.name)
          ? potentialTarget
          : undefined;
        if (this._processPhase1Attack(action, effect, contributingAnimations, target, isFromReplacement)) {
          return true; // Re-queue signaled
        }
      } else if (effect.type === 'ATTACK' && effect.attackTiming === 'CONFLICT') {
        logger.debug(`  - Processing ATTACK(CONFLICT) effect. Conflict:`, conflict);
        let target: any | undefined;
        if (conflict) {
          const potentialTargets = conflict.participants.filter(p =>
            p.entity.id !== action.entity.id &&
            p.entity.getComponent(Faction)?.name !== action.entity.getComponent(Faction)?.name
          );

          if (potentialTargets.length > 0) {
            const stationaryTargetAction = potentialTargets.find(p =>
              p.startPos?.is(conflict.contestedHex)
            );

            if (stationaryTargetAction) {
              target = stationaryTargetAction.entity;
              logger.debug(`    - Found stationary enemy target: ${target.toString()}`);
            } else {
              // No stationary target, must be a collision of movers. Deterministically pick one.
              const winningTargetAction = TickResolutionHelpers.resolveTie(potentialTargets);
              target = winningTargetAction.entity;
              logger.debug(`    - No stationary enemy. Selected moving target via tie-break: ${target.toString()}`);
            }
          }
        }
        if (this._processPhase1Attack(action, effect, contributingAnimations, target, isFromReplacement)) {
          return true; // Re-queue signaled
        }
      }
    }
    return false;
  }

  private _finalizeMoveOutcome(action: Action, contributingAnimations: { plan: AnimationStep[][], strategy?: string }[]) {
    const moveOutcome = action.outcomes.find(o => o.type === ActionType.MOVE);
    if (moveOutcome && action.startPos) {
      const occupant = this.projectedOccupancy.getOccupant(moveOutcome.coords);
      logger.debug(`  - Validating MOVE to ${moveOutcome.coords.key}. Occupant at destination: ${occupant ? occupant.toString() : 'null'}`);
      if (occupant && occupant.id !== action.entity.id) {
        // Bonk: the move is invalid.
        logger.debug(`  - Bonk detected. Removing MOVE outcome.`);
        action.removeMoveOutcome();
        logger.debug(`  - Removing move animations from contributors.`);
        for (let i = contributingAnimations.length - 1; i >= 0; i--) {
          const anim = contributingAnimations[i];
          // A bit broad, but any animation from a move effect is likely move-related.
          // e.g. 'MOVE', 'MOVE_SWIPE'
          const isMoveAnimation = anim.plan.flat().some(step => step.type.startsWith('MOVE'));
          if (isMoveAnimation) {
            contributingAnimations.splice(i, 1);
          }
        }
      }
    }
  }

  private _processPhase1Attack(action: Action, effect: AttackEffect, contributingAnimations: { plan: AnimationStep[][], strategy?: string }[], target: any | undefined, isFromReplacement: boolean): boolean {
    if (target) { // HIT / BLOCKED
      if (!this._isBlocked(action, target, effect)) { // HIT
        action.addAttackOutcome(target, effect.damage);
        if (effect.attackTiming === 'ANY') {
          this._spentAnyEffects.add(effect);
        }
        if (effect.animationPlan) contributingAnimations.push({ plan: effect.animationPlan, strategy: effect.animationMergeStrategy });
        const targetStats = target.getComponent(ActorStats);
        if (targetStats && (targetStats.health - effect.damage <= 0)) {
          this.pendingDeaths.add(target.id);
          logger.debug(`    - Attack is lethal. Marking entity ${target.id} as pending death.`);
        }
      } else { // BLOCKED
        if (effect.onBlocked?.animationPlan) contributingAnimations.push({ plan: effect.onBlocked.animationPlan });
      }
    } else { // WHIFF
      if (isFromReplacement) {
        logger.debug(`    - ATTACK from replacement proposal whiffed. Signaling for action re-queue.`);
        return true; // Signal to revert and requeue.
      } else {
        logger.debug(`    - Native or Augment ATTACK whiffed. Action will not be re-queued.`);
      }
    }
    return false; // No need to requeue.
  }

  private _resolveActionForPhase2(action: Action): void {
    logger.debug(`  - _resolveActionForPhase2 for action:`, action)
    if (this.pendingDeaths.has(action.entity.id)) {
      logger.debug(`    - Action skipped because entity ${action.entity.id} is pending death.`);
      return;
    }
    const allEffects = this.mergedEffects.get(action.entity.id);
    if (!allEffects) return;

    for (const effect of allEffects) {
      if (effect.type !== 'ATTACK' || (effect.attackTiming !== 'END' && effect.attackTiming !== 'BOTH' && effect.attackTiming !== 'ANY')) {
        continue;
      }
      if (effect.attackTiming === 'ANY' && this._spentAnyEffects.has(effect)) {
        continue; // This 'ANY' attack already hit in Phase 1.
      }
      logger.debug(`  - _resolveActionForPhase2 for effect:`, effect);

      const outcome = TickResolutionHelpers.getPhase2AttackOutcome(effect, action, this.initialOccupancy, this.projectedOccupancy);

      switch (outcome.type) {
        case 'HIT':
          if (!this._isBlocked(action, outcome.target, effect)) {
            action.addAttackOutcome(outcome.target, effect.damage);
            this._mergeAnimationIntoPlan(action, effect.animationPlan, effect.animationMergeStrategy);
            const targetStats = outcome.target.getComponent(ActorStats);
            if (targetStats && (targetStats.health - effect.damage <= 0)) {
              this.pendingDeaths.add(outcome.target.id);
              logger.debug(`    - Attack is lethal. Marking entity ${outcome.target.id} as pending death.`);
            }
          } else {
            this._mergeAnimationIntoPlan(action, effect.onBlocked?.animationPlan);
          }
          break;

        case 'NEAR_MISS': {
          const missAnimations: AnimationStep[][] = [];
          if (effect.onMiss) {
            for (const contingentEffect of effect.onMiss) {
              if (contingentEffect.type === 'MISS') {
                action.addMissOutcome(contingentEffect.target);
                if (contingentEffect.animationPlan) {
                  missAnimations.push(...contingentEffect.animationPlan);
                }
              }
            }
          }
          if (missAnimations.length > 0) {
            this._mergeAnimationIntoPlan(action, missAnimations);
          } else {
            this._mergeAnimationIntoPlan(action, effect.animationPlan, effect.animationMergeStrategy);
          }
          break;
        }

      }
    }
  }

  private _mergeAnimationIntoPlan(action: Action, animationPlan: AnimationStep[][] | undefined, strategy?: string): void {
    if (!animationPlan || animationPlan.length === 0) return;

    if (strategy === 'REPLACE') {
      action.animationPlan = JSON.parse(JSON.stringify(animationPlan));
      logger.debug(`  - Animation plan REPLACED.`);
    } else { // APPEND or undefined. Appending adds new stages to the animation.
      action.animationPlan.push(...JSON.parse(JSON.stringify(animationPlan)));
      logger.debug(`  - Animation plan APPENDED.`);
    }
  }

  private _isBlocked(attackerAction: Action, defender: any, attackEffect: AttackEffect): boolean {
    const defenderDefinition = this._mergedDefinitionsForLoop.get(defender.id);
    const defenderEffects = defenderDefinition ? defenderDefinition.effects : [];
    const blockEffects = defenderEffects.filter((e): e is BlockEffect => e.type === 'BLOCK');
    if (blockEffects.length === 0) return false;

    // Determine defender's final bearing for the tick. This is their starting bearing,
    // potentially overridden by a TURN effect in their final merged action definition.
    let defenderBearing = defender.getComponent(HexPosition)?.bearing;
    if (!defenderBearing) return false;

    if (defenderEffects) {
      const turnEffect = defenderEffects.find(e => e.type === 'TURN');
      if (turnEffect && 'bearing' in turnEffect) {
        defenderBearing = turnEffect.bearing;
      }
    }

    // Determine absolute bearing of the attack. An attack comes FROM the attacker's
    // start hex TO the target hex of the effect.
    const attackSourceHex = attackerAction.startPos;
    if (!attackSourceHex) return false;

    // The bearing of the attack is from the defender's hex pointing towards the attacker's starting hex.
    const attackBearing = HexBearing.all.find(b => attackEffect.attackHex.neighbor(b).is(attackSourceHex));
    if (!attackBearing) return false; // Not an adjacent attack

    // Check if attack bearing is in one of the shielded bearings.
    for (const blockEffect of blockEffects) {
      const shieldedBearings = blockEffect.fromBearings.map(offset =>
        HexBearing.next(defenderBearing, offset)
      );
      if (shieldedBearings.includes(attackBearing)) {
        logger.debug(`Attack from ${attackerAction.entity.id} on ${defender.id} was BLOCKED.`);
        return true;
      }
    }

    return false;
  }

  private _resolvePreemptiveBonks(): boolean {
    let bonkedAnAction;
    let resolvedAny = false;
    do {
      bonkedAnAction = false;
      const blockedMover: Action | undefined = this.pendingActions.find(action => {
        if (action.type !== ActionType.MOVE || !action.details.coords) return false;
        const occupant = this.projectedOccupancy.getOccupant(action.details.coords);
        return occupant && occupant.id !== action.entity.id && this.isActionForEntityResolved(occupant.id);
      });

      if (blockedMover) {
        const blockedMoverAction: Action = blockedMover;
        const occupant = this.projectedOccupancy.getOccupant(blockedMoverAction.details.coords!)!;
        logger.debug(`Pre-emptive bonk: ${blockedMoverAction.toString()} is blocked by resolved entity ${occupant.toString()}. Forcing IDLE.`);

        blockedMoverAction.forceIdle();
        blockedMoverAction.resolve();
        this.resolvedActions.push(blockedMoverAction);
        resolvedAny = true;

        this.pendingActions = this.pendingActions.filter(a => a.id !== blockedMoverAction.id);
        bonkedAnAction = true;
      }
    } while (bonkedAnAction);
    return resolvedAny;
  }
}
